// Hook owning the field room-delete state machine (ADR-015), extracted
// from building-capture-content.tsx so that file stays under the
// lint:max-lines cap — same split rationale as useCancelSession.
//
// Responsibilities:
//   - track the room pending confirmation (open/close the dialog),
//   - keep the set of optimistically-removed room ids so the list updates
//     the instant the user confirms,
//   - dispatch the `deleteSpace` mutation, and reconcile:
//       success → keep the room removed, return the recomputed building
//                 status so the caller can flip its status state,
//       error   → restore the room (pull its id back out of the set) and
//                 surface the message via `.error`.
//
// The hook does NOT navigate or own building-status state — the caller
// applies the returned status. Online-first: the dispatch routes through
// the capture client's REST adapter today; the offline path is #596.

import { useCallback, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

import type { DroppedEvent } from '@/hooks/powersync/upload-events'
import { subscribeToDrops } from '@/hooks/powersync/upload-events'

import { ApiError } from '../../hooks/api'
import type { CaptureClient } from '../client/capture-client.types'

import type { BuildingStatus } from '@citycare/shared'

export interface DeleteRoomTarget {
  id: string
  displayName: string
  items: ReadonlyArray<{ quantity: string }>
}

export interface UseDeleteRoomInput {
  captureClient: Pick<CaptureClient, 'dispatch'>
  /** The owning capture session id (scopes + authorises the delete). */
  sessionId: string
}

export interface UseDeleteRoomResult {
  /** The room currently pending confirmation, or null when no dialog open. */
  target: DeleteRoomTarget | null
  /** Ids of rooms optimistically removed from the list. */
  deletedIds: ReadonlySet<string>
  isDeleting: boolean
  error: string | null
  /** Open the confirm dialog for a room. */
  open: (room: DeleteRoomTarget) => void
  /** Dismiss without deleting (no-op while a delete is in flight). */
  close: () => void
  /**
   * Confirm + dispatch the delete. Optimistically removes the room, then
   * reconciles. Resolves with the recomputed building status on success
   * (null when the server didn't return one — e.g. the 404-idempotent
   * path), or null on failure (message exposed via `.error`).
   */
  confirm: () => Promise<BuildingStatus | null>
}

function describeDeleteError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export function useDeleteRoom({
  captureClient,
  sessionId,
}: UseDeleteRoomInput): UseDeleteRoomResult {
  const [target, setTarget] = useState<DeleteRoomTarget | null>(null)
  const [deletedIds, setDeletedIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  )
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A delete can be REVERTED long after `confirm` resolved. The offline
  // dispatch resolves immediately against local SQLite (the room is
  // optimistically removed), but the connector may later find the building was
  // completed elsewhere and DROP the queued delete
  // (connector-dispatch-space-delete.ts, failed_precondition →
  // `delete_reverted`). The room is intact server-side and re-syncs, so undo
  // the optimistic removal here — otherwise the room stays hidden behind
  // `deletedIds` until the page remounts (Codex P2 on #631). Scoped to spaces +
  // `delete_reverted`; other drop reasons mean genuine loss, not a room to
  // restore. (No-op for the REST adapter, which never emits drops.)
  useEffect(() => {
    return subscribeToDrops((event: DroppedEvent) => {
      if (event.table !== 'spaces' || event.reason !== 'delete_reverted') {
        return
      }
      setDeletedIds((prev) => {
        if (!prev.has(event.id)) return prev
        const next = new Set(prev)
        next.delete(event.id)
        return next
      })
    })
  }, [])

  const open = useCallback((room: DeleteRoomTarget) => {
    setError(null)
    setTarget(room)
  }, [])

  const close = useCallback(() => {
    if (isDeleting) return
    setTarget(null)
    setError(null)
  }, [isDeleting])

  const confirm = useCallback(async (): Promise<BuildingStatus | null> => {
    if (target === null || isDeleting) return null
    const room = target
    setIsDeleting(true)
    setError(null)
    // Optimistic removal — drop the room from the list immediately.
    setDeletedIds((prev) => {
      const next = new Set(prev)
      next.add(room.id)
      return next
    })
    try {
      const result = await captureClient.dispatch({
        kind: 'deleteSpace',
        sessionId,
        spaceId: room.id,
      })
      if (result.kind !== 'deleteSpace') {
        throw new Error('Unexpected delete result kind')
      }
      setIsDeleting(false)
      setTarget(null)
      return result.buildingStatus
    } catch (err) {
      // Reconcile on error: restore the room + surface the message.
      setDeletedIds((prev) => {
        const next = new Set(prev)
        next.delete(room.id)
        return next
      })
      setIsDeleting(false)
      setError(describeDeleteError(err))
      // Report to Sentry so a delete failure is VISIBLE next time. Before this,
      // the ONLY signal was the message rendered in the dialog (the surveyor's
      // screen) — the writeTransaction this-binding crash ("Cannot read
      // properties of undefined (reading 'waitForReady')") reached every
      // surveyor in the field with zero telemetry because this catch swallowed
      // it into `.error` text. An ApiError is an expected server-side
      // validation/precondition (e.g. the completion gate) → `warning`;
      // anything else is an unexpected client/local fault → `error`. Room id +
      // session id only; no captured-count values or display names (PII-safe,
      // matching the connector dispatch breadcrumbs).
      //
      // Telemetry is BEST-EFFORT (audit-1-#1 / Codex P2): confirm() is contracted
      // to RESOLVE `null` on a delete failure, never reject —
      // building-capture-content.tsx invokes it via `void handleConfirmDelete()`
      // with no rejection handler. A throwing or misconfigured Sentry SDK (test
      // mocks, a broken transport) must NOT turn an expected failure (e.g. a 403
      // completion-gate precondition) into an unhandled rejection, so the capture
      // is wrapped. State (isDeleting/error/deletedIds) is already committed above,
      // so swallowing here cannot wedge the UI.
      try {
        Sentry.captureException(err, {
          level: err instanceof ApiError ? 'warning' : 'error',
          extra: {
            reason: 'capture_room_delete_failed',
            roomId: room.id,
            sessionId,
          },
        })
      } catch {
        // Telemetry must never change delete behaviour — drop a Sentry fault.
      }
      return null
    }
  }, [captureClient, sessionId, target, isDeleting])

  return { target, deletedIds, isDeleting, error, open, close, confirm }
}
